/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  batch,
  onConnect,
  reaction,
  withAssign,
} from '@reatom/framework'

import { Array, AsyncStateN, pipe, Result } from '@blade/std-lib'

import {
  reatomDataTableFilters,
  type TableFilterOptions,
} from './reatom-data-table-filters'
import { reatomDataTablePagination } from './reatom-data-table-pagination'
import { reatomDataTableSorting } from './reatom-data-table-sorting'
import { reatomDataTableState } from './reatom-data-table-state'
import * as TableFilter from './table-filter'

import type { TablePagination } from './reatom-data-table-pagination'
import type {
  TableSorting,
  TableSortingOptions,
} from './reatom-data-table-sorting'
import type { DataTableStateArgs, TableData } from './reatom-data-table-state'
import type { Column } from '../ui/data-table-types'
import type { Atom, AtomMut, Ctx } from '@reatom/framework'

const noopParamsAtom = atom<object>({}, 'noopParamsAtom')

export type UpsertItemsOptions = {
  insertMode?: 'prepend' | 'append'
  /**
   * @default true
   */
  applyClientSideSorting?: boolean
  /**
   * @default true
   */
  applyClientSideFilters?: boolean
}

export type TableUpdateTrigger =
  | 'params'
  | 'sorting-filters'
  | 'loadMore'
  | 'load'

export const reatomDataTable = <
  D extends object,
  ColumnId extends string = string,
  Params extends object = object,
  Context extends object | undefined = undefined,
>({
  name,
  loadItems,
  limit = 20,
  initialSorting,
  initialFilters = {},
  stateAtom,
  paramsAtom = noopParamsAtom as unknown as Atom<Params>,
  getId,
  columns,
  initialItems,
  overrideState,
  onConnectStrategy = 'load',
  maxItems,
}: {
  loadItems?: (
    ctx: Ctx,
    params: {
      params: Params
      sorting: TableSorting<ColumnId> | undefined
      pagination: TablePagination
      filters: TableFilter.TableFilters<ColumnId>
    },
  ) => Promise<Result.Result<TableData<D>, unknown>>
  limit?: number
  paramsAtom?: Atom<Params>
  getId: (item: D) => string | number
  columns: Array<Column<D, ColumnId, Context>>
  name: string
  overrideState?: Atom<AsyncStateN.AsyncStateN<unknown, TableData<D>>>
  onConnectStrategy?: 'load' | 'refresh' | 'none'
} & TableSortingOptions<ColumnId> &
  TableFilterOptions<ColumnId> &
  DataTableStateArgs<D>) => {
  //#region State

  const stateModel = reatomDataTableState<D>({
    name,
    initialItems,
    stateAtom,
    getId,
    maxItems,
  })
  const internalUpsertItems = stateModel.upsertItems

  const paginationAtom = reatomDataTablePagination({ limit })

  const sortingAtom = reatomDataTableSorting<D, ColumnId, Context>({
    initialSorting,
    columns,
    stateModel,
  })

  const filtersAtom = reatomDataTableFilters<D, ColumnId, Context>({
    initialFilters,
    columns,
    stateModel,
  })

  const loadMoreStateAtom = atom<
    AsyncStateN.AsyncStateN<unknown, TableData<D>>
  >(AsyncStateN.notInitiated(), `${name}.loadMoreStateAtom`)

  //#endregion State

  //#region Load Actions

  const handleLoadResultAction = action(
    async (ctx, result: Result.Result<TableData<D>, unknown>) => {
      stateModel.setState(ctx, AsyncStateN.fromResult(result))
      if (Result.isSuccess(result)) {
        paginationAtom.handleData(ctx, result.success, { offset: 0 })
      }
    },
    `${name}.handleLoadResultAction`,
  )

  const loadItemsAction = reaction(async (ctx) => {
    if (!loadItems) {
      return
    }
    stateModel.setState(ctx, AsyncStateN.loading())
    paginationAtom.reset(ctx)

    const sorting = ctx.get(sortingAtom)
    const filters = ctx.get(filtersAtom)
    const params = ctx.spy(paramsAtom)
    const ctx_ = {
      ...ctx,
      spy: ctx.get,
    }
    const result = await ctx.schedule(() =>
      loadItems(ctx_, {
        sorting,
        pagination: {
          limit,
          offset: 0,
          cursor: undefined,
        },
        filters: filters as TableFilter.TableFilters<ColumnId>,
        params,
      }),
    )

    if (
      params !== ctx.get(paramsAtom) ||
      sorting !== ctx.get(sortingAtom) ||
      filters !== ctx.get(filtersAtom)
    ) {
      return
    }

    handleLoadResultAction(ctx, result)
  }, `${name}.loadItemsAction`)

  /**
   * We cannot reuse load here because it will start a reaction
   */
  const refreshAction = action(async (ctx) => {
    if (!loadItems) {
      return
    }
    const sorting = ctx.get(sortingAtom)
    const filters = ctx.get(filtersAtom)
    const params = ctx.get(paramsAtom)
    const state = ctx.get(stateModel)
    if (AsyncStateN.isLoading(state)) {
      return
    }
    if (!AsyncStateN.isSuccess(state)) {
      loadItemsAction(ctx)
      return
    }
    const result = await ctx.schedule(() =>
      loadItems(ctx, {
        sorting,
        pagination: {
          limit,
          offset: 0,
          cursor: undefined,
        },
        filters: filters as TableFilter.TableFilters<ColumnId>,
        params,
      }),
    )

    if (Result.isSuccess(result)) {
      handleLoadResultAction(ctx, result)
    }
  }, `${name}.refreshAction`)

  const loadMoreAction = action(async (ctx) => {
    if (!loadItems) {
      return
    }
    const state = ctx.get(stateModel)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }
    const sorting = ctx.get(sortingAtom)
    const filters = ctx.get(filtersAtom)

    const { offset: currentOffset, cursor } = ctx.get(paginationAtom)
    const nextOffset = currentOffset + limit

    loadMoreStateAtom(ctx, AsyncStateN.loading())

    const result = await ctx.schedule(() =>
      loadItems(ctx, {
        sorting,
        pagination: {
          limit,
          offset: nextOffset,
          cursor,
        },
        filters: filters as TableFilter.TableFilters<ColumnId>,
        params: ctx.get(paramsAtom),
      }),
    )

    batch(ctx, () => {
      loadMoreStateAtom(ctx, AsyncStateN.fromResult(result))
      if (Result.isSuccess(result)) {
        paginationAtom.handleData(ctx, result.success, { offset: nextOffset })

        const itemsState = ctx.get(stateModel)
        if (AsyncStateN.isSuccess(itemsState)) {
          const updatedItems = pipe(
            [...itemsState.value.data, ...result.success.data],
            Array.uniqBy(getId),
          )

          stateModel.setState(
            ctx,
            AsyncStateN.success({
              data: updatedItems,
              hasNext: result.success.hasNext,
              total: result.success.total,
            }),
          )
        }
      }
    })
  }, `${name}.loadMoreAction`)

  //#endregion Load Actions

  // #region Upsert Items Actions

  const upsertItemsAction = action(
    (
      ctx,
      items: Array<D>,
      options?: {
        insertMode?: 'prepend' | 'append'
        applyClientSideSorting?: boolean
        applyClientSideFilters?: boolean
      },
    ) => {
      if (items.length === 0) {
        return
      }
      const state = ctx.get(stateModel)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }

      // TODO: Add items to temporary buffer if initial items loading
      // We don't handle client side sorting and filtering here
      const {
        insertMode = 'append',
        applyClientSideFilters = true,
        applyClientSideSorting = true,
      } = options ?? {}

      internalUpsertItems(ctx, items, {
        insertMode,
      })

      if (applyClientSideFilters) {
        filtersAtom.applyClientSideFilters(ctx)
      }
      if (applyClientSideSorting) {
        sortingAtom.applyClientSideSorting(ctx)
      }
    },
    `${name}.upsertItemsAction`,
  )

  // #endregion Upsert Items Actions

  //#region Sorting & Filtering Actions

  const setSortingAction = action(
    (ctx, sorting: TableSorting<ColumnId> | undefined) => {
      sortingAtom(ctx, sorting)
      loadItemsAction(ctx)
      return
    },
    `${name}.setSortingAction`,
  )

  const toggleSortingAction = action((ctx, column: ColumnId) => {
    const nextSorting = sortingAtom.toggleSorting(ctx, column)
    setSortingAction(ctx, nextSorting)
  }, `${name}.toggleSortingAction`)

  const setColumnFilterAction = action(
    (
      ctx,
      column: ColumnId,
      filter: TableFilter.TableFilter<ColumnId> | undefined,
    ) => {
      const filters = ctx.get(filtersAtom)
      const existingFilter = filters[column]

      if (TableFilter.eq(existingFilter, filter)) {
        return
      }

      const newFilters = {
        [column]: filter,
      } as Record<ColumnId, TableFilter.TableFilter<ColumnId> | undefined>

      setColumnsBatchFilterAction(ctx, newFilters)
    },
    `${name}.setColumnFilterAction`,
  )

  const setColumnsBatchFilterAction = action(
    (
      ctx,
      newFilters: Record<
        ColumnId,
        TableFilter.TableFilter<ColumnId> | undefined
      >,
    ) => {
      const filters = ctx.get(filtersAtom)

      const updatedFilters: TableFilter.TableFilters<ColumnId> = {
        ...filters,
        ...newFilters,
      }

      filtersAtom(ctx, updatedFilters)
      loadItemsAction(ctx)
    },
    `${name}.setColumnsBatchFilterAction`,
  )

  const clearColumnFilterAction = action((ctx, column: ColumnId) => {
    setColumnFilterAction(ctx, column, undefined)
  }, `${name}.clearColumnFilterAction`)

  const clearColumnsFiltersAction = action((ctx, columns: Array<ColumnId>) => {
    setColumnsBatchFilterAction(
      ctx,
      Object.fromEntries(columns.map((x) => [x, undefined])) as Record<
        ColumnId,
        undefined
      >,
    )
  }, `${name}.clearColumnsFiltersAction`)

  //#endregion Sorting & Filtering Actions

  //#region Computed

  const countAtom = atom((ctx) => {
    const state = ctx.spy(stateModel)
    return AsyncStateN.isSuccess(state) ? state.value.data.length : 0
  }, `${name}.countAtom`)

  const dataAtom = atom((ctx) => {
    if (overrideState) {
      return ctx.spy(overrideState).value?.data ?? []
    }
    const state = ctx.spy(stateModel)
    return state.value?.data ?? []
  }, `${name}.dataAtom`)

  //#endregion Computed

  //#region Hooks

  onConnect(stateModel, (ctx) => {
    const state = ctx.get(stateModel)

    if (onConnectStrategy === 'none') {
      return
    }

    if (!AsyncStateN.isSuccess(state)) {
      loadItemsAction(ctx)
    }
  })

  //#endregion Hooks

  return stateModel.pipe(
    withAssign(() => ({
      load: loadItemsAction,
      loadMore: loadMoreAction,
      refresh: refreshAction,
      hasMoreItems: paginationAtom.hasMoreItems,
      loadMoreState: loadMoreStateAtom,
      setSorting: setSortingAction,
      toggleSorting: toggleSortingAction,
      sorting: sortingAtom,
      count: countAtom,
      setColumnFilter: setColumnFilterAction,
      clearColumnFilter: clearColumnFilterAction,
      clearColumnsFilters: clearColumnsFiltersAction,
      filters: filtersAtom,
      getId,
      upsertItems: upsertItemsAction,
      columns,
      setState: stateModel.setState,
      overrideState,
      data: dataAtom,
    })),
  )
}

export interface DataTableModel<
  D extends object = object,
  Column extends string = string,
  Params extends object = object,
  Context extends object | undefined = undefined,
> extends ReturnType<typeof reatomDataTable<D, Column, Params, Context>> {}

export type DataTableItemsState<D extends object> = AtomMut<
  AsyncStateN.AsyncStateN<unknown, { data: Array<D> }>
>
